<template>
  <HeaderCard>
    <template v-slot:header>
      <span class="flex items-stretch justify-between">
        <span
          class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-sm w-full ml-xs sm:ml-md"
        >
          <span class="flex flex-col sm:flex-row sm:items-center gap-sm">
            <span
              class="leading-lg"
              data-test="party-name"
            >
              {{ $t('ui.envelopes.cards.partyRoleCard.party') }}
              {{ partyIndex + 1 }}
            </span>

            <template v-if="selectedTab === 'name'">
              <span class="hidden sm:inline text-midGray text-12">|</span>
              <button
                @click="$emit('addMyselfAsParty', partyIndex)"
                class="naked !text-12 text-midGray cursor-pointer underline hover:text-black"
                data-test="add-myself"
              >
                {{ $t('ui.envelopes.cards.partyRoleCard.addMyselfAsParty') }}
              </button>
            </template>
          </span>

          <span>
            <ToggleControl
              :value="party.role !== 'signer'"
              :label="$t('ui.envelopes.cards.partyRoleCard.receiveOnlyCopy')"
              @toggle="$emit('toggleCopyOnly', partyIndex)"
              class="flex-row-reverse sm:flex-row text-12"
              :disabled="roles.hasSignerRole(partyIndex)"
              data-test="only-copy"
            />
          </span>
        </span>

        <span class="flex gap-sm items-center h-lg ml-sm">
          <XMarkControl
            @click.native="$emit('remove', partyIndex)"
            :disabled="!canRemove"
            data-test="remove"
          />
        </span>
      </span>
    </template>

    <template v-slot:content>
      <div class="relative">
        <TabList
          :tabs="tabs"
          v-model="selectedTab"
          @dismiss="maybeRemoveTab($event)"
        />

        <div
          :class="`absolute top-0 right-base h-0 flex items-center z-10
            sm:right-lg sm:top-0 sm:h-full`"
        >
          <BaseDropdown
            :text="$t('ui.envelopes.create.steps.parties.add')"
            :value="options"
            class="absolute bottom-0 right-0 sm:relative"
            buttonClass="!py-xs !px-base !mb-twelve sm:!py-sm sm:!mb-0"
          />
        </div>
      </div>

      <TabGroup
        :tabs="tabs"
        :selected="selectedTab"
        class="mx-base sm:mx-lg"
      >
        <template v-slot:name>
          <div class="flex flex-col md:flex-row items-stretch gap-sm mb-lg">
            <div class="flex flex-col gap-sm w-full">

              <ContactAutocomplete
                v-model.trim="party.title"
                @blur="validate('title')"
                @selected="handleContactSelection"
                :hasError="hasErrors('title')"
              />

              <ValidationError
                :hasError="hasErrors('title')"
                :errorMessage="getError('title')"
              />
            </div>

            <div class="flex flex-col gap-sm w-full">
              <FloatingLabelInput
                v-model.trim="party.email"
                type="email"
                :aria-label="
                  $t('ui.envelopes.cards.partyRoleCard.emailAddress')
                "
                :placeholder="
                  $t('ui.envelopes.cards.partyRoleCard.emailAddress')
                "
                :hasError="hasErrors('email')"
                class="!min-w-0 !m-0"
                data-ec=""
                data-et="email_input"
                @blur="validate('email')"
              />

              <ValidationError
                :hasError="hasErrors('email')"
                :errorMessage="getError('email')"
              />
            </div>
          </div>

          <PartyRoleSelector
            :roles="roles"
            :partyIndex="partyIndex"
            @toggle="
              (partyIndex, roleId) =>
                $emit('toggleRoleSelected', partyIndex, roleId)
            "
            class="!p-0 mb-lg"
          />
        </template>

        <template v-slot:message>
          <div class="flex flex-col gap-sm w-full mb-lg">
            <PersonalMessageTab
              v-if="party.message !== undefined"
              v-model.trim="party.message"
              :errors="getErrors('message')"
              @input="hasErrors('message') && validate('message')"
              @blur="validate('message')"
              @remove="maybeRemoveTab('message')"
            />
          </div>
        </template>

        <template v-slot:password>
          <div class="flex flex-col gap-sm w-full mb-lg">
            <PasswordTab
              v-if="party.password !== undefined"
              v-model.trim="party.password"
              :errors="getErrors('password')"
              @input="hasErrors('password') && validate('password')"
              @blur="validate('password')"
              @remove="maybeRemoveTab('password')"
            />
          </div>
        </template>
      </TabGroup>

      <RemoveTabModal
        ref="removeMessageModal"
        tabName="Personal Message"
        @remove="removeTab('message')"
      />
      <RemoveTabModal
        ref="removePasswordModal"
        tabName="Password"
        @remove="removeTab('password')"
      />
    </template>
  </HeaderCard>
</template>

<script setup>
// @ts-check

import { computed, defineProps, ref, toRef } from 'vue';
import { useReactiveSet } from 'vue-reactive-collection';

import { useErrors } from '@/composables/envelopes/validation';
import i18n from '@/locales';

import { HeaderCard } from '@/components/library/cards';
import { BaseDropdown } from '@/components/library/dropdowns/BaseDropdown';
import ValidationError from '@/components/library/error/ValidationError/ValidationError.vue';
import { PartyRoleSelector } from '@/components/library/partyRoleSelectors/PartyRoleSelector';
import { TabGroup } from '@/components/library/tabs/TabGroup';
import { TabList } from '@/components/library/tabs/TabList';
import FloatingLabelInput from '@/components/tailwind/forms/FloatingLabelInput.vue';
import {
  ToggleControl,
  XMarkControl,
} from '@/components/ui/common/cardControls';
import { PasswordTab } from '@/components/ui/envelopeCreate/steps/addRecipients/passwordTab';
import { RemoveTabModal } from '@/components/ui/envelopeCreate/modals/RemoveTabModal';
import PersonalMessageTab
  from '@/components/ui/envelopeCreate/steps/addRecipients/personalMessageTab/PersonalMessageTab.vue';
import ContactAutocomplete from '@/components/ui/contacts/ContactAutocomplete/ContactAutocomplete.vue';

/** @typedef {import('@/composables/roles/roles').TRoles} TRoles */
/** @typedef {import('@/data/schema/parties').TNewOrSavedParty} TNewOrSavedParty */
/** @typedef {import('@/components/library/tabs/TabItem').TTab} TTab */
/** @typedef {import('zod').ZodFormattedError<any>} ZodFormattedErrorAny */

const props = defineProps({
  party: {
    /** @type {import('vue').PropType<TNewOrSavedParty>} */
    type: Object,
    required: true,
  },
  partyIndex: {
    type: Number,
    required: true,
  },
  roles: {
    /** @type {import('vue').PropType<TRoles>} */
    type: Object,
    required: true,
  },
  validate: {
    type: Function,
    required: true,
  },
  errors: {
    /** @type {import('vue').PropType<ZodFormattedErrorAny|undefined>} */
    type: Object,
    required: false,
  },
  canRemove: {
    type: Boolean,
    required: true,
  },
});

const errors = toRef(props, 'errors');
const { hasErrors, getErrors, getError } = useErrors(errors);

/** @type {import('vue').Ref<InstanceType<RemoveTabModal>|null>} */
const removePasswordModal = ref(null);
/** @type {import('vue').Ref<InstanceType<RemoveTabModal>|null>} */
const removeMessageModal = ref(null);

const selectedTab = ref('name');

const baseTabs = computed(() => ([
  {
    title: i18n.t('ui.envelopes.create.steps.parties.nameTab.title'),
    id: 'name',
  },
  {
    title: i18n.t('ui.envelopes.create.steps.parties.messageTab.title'),
    id: 'message',
    dismissible: true,
  },
  {
    title: i18n.t('ui.envelopes.create.steps.parties.passwordTab.title'),
    id: 'password',
    dismissible: true,
  },
]));

const activeTabs = useReactiveSet(baseTabs.value.map((tab) => {
  if (!tab.dismissible) {
    return tab.id;
  } else if (props.party[tab.id]) {
    return tab.id;
  }

  return undefined;
}));

/** @param {string} tabId */
function removeTab(tabId) {
  if (activeTabs.value.has(tabId)) {
    activeTabs.value.delete(tabId);
    props.party[tabId] = undefined;
  }
  if (tabId === selectedTab.value) {
    selectedTab.value = 'name';
  }
}

/** @param {string} tabId */
function maybeRemoveTab(tabId) {
  const modal = tabId === 'password' ? removePasswordModal : removeMessageModal;

  if (modal.value) {
    modal.value.open();
  }
}

/** @param {string} tabId */
function tabHasError(tabId) {
  if (tabId === 'name') {
    return hasErrors('title') || hasErrors('email');
  }
  return hasErrors(tabId);
}

function handleContactSelection(event) {
  if (event) {
    props.party.title = event.title;
    props.party.email = event.email;
  }
}

const tabs = computed(() =>
  baseTabs.value
    .filter(tab => activeTabs.value.has(tab.id))
    .map(tab => ({
      ...tab,
      hasError: tabHasError(tab.id),
    })));

const options = computed(() => [
  {
    action: () => {
      activeTabs.value.add('message');
      selectedTab.value = 'message';
      props.party.message = '';
    },
    text: i18n.t('ui.envelopes.create.steps.parties.messageTab.title'),
    disabled: tabs.value.some(tab => tab.id === 'message'),
  },
  {
    action: () => {
      activeTabs.value.add('password');
      selectedTab.value = 'password';
      props.party.password = '';
    },
    text: i18n.t('ui.envelopes.create.steps.parties.passwordTab.title'),
    disabled: tabs.value.some(tab => tab.id === 'password'),
  },
]);
</script>
